import React, { useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCart } from '../../context/CartContext';
import {
    MantenimientoPreventivo,
    Diagnostico,
    Garantias,
    Reconstrucciones,
    Livelink
} from '../../assets/images';

const Services = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { addToCart, cartItems } = useCart();

    const maintenanceRef = useRef(null);
    const diagnosticRef = useRef(null);
    const warrantyRef = useRef(null);
    const reconstructionsRef = useRef(null);
    const livelinkRef = useRef(null);

    const sectionRefs = {
        maintenance: maintenanceRef,
        diagnostic: diagnosticRef,
        warranty: warrantyRef,
        reconstructions: reconstructionsRef,
        livelink: livelinkRef,
    };

    useEffect(() => {
        if (!location.hash) return;

        const id = location.hash.replace('#', '');
        const ref = sectionRefs[id];

        if (ref && ref.current) {
            const t = setTimeout(() => {
                ref.current.scrollIntoView({ behavior: 'smooth' });
            }, 100);
            return () => clearTimeout(t);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.hash]);

    const services = [
        {
            id: 'maintenance',
            title: 'Mantenimiento Preventivo',
            image: MantenimientoPreventivo,
            alt: 'Servicio de Mantenimiento Preventivo JCB Azteca - Programas para prevenir fallas y prolongar vida útil',
            description: 'Programas de mantenimiento diseñados para prevenir fallas y prolongar la vida útil de tu maquinaria. JCB POWER SYSTEMS comenzó la producción del motor JCB DIESEL MAX de 4 cilindros en 2004, y fue la culminación de años de investigación, desarrollo e inversión.',
            features: [
                'Inspecciones periódicas programadas',
                'Cambio de filtros y lubricantes',
                'Ajustes y calibraciones',
                'Reporte detallado de condiciones',
                'Programación de citas para mantenimiento'
            ],
            cta: 'Solicitar Mantenimiento'
        },
        {
            id: 'diagnostic',
            title: 'Diagnóstico y Reparación de Fallas',
            image: Diagnostico,
            alt: 'Servicio de Diagnóstico y Reparación JCB Azteca - Herramientas avanzadas y personal certificado',
            description: 'Generamos diagnósticos precisos y reparaciones especializadas de nuestras maquinarias JCB. Nuestras herramientas tecnológicas y personal altamente capacitado ayudan a nuestros clientes a aumentar sus tiempos de operación.',
            features: [
                'Herramientas de diagnóstico avanzadas',
                'Personal certificado',
                'Reporte detallado de fallas',
                'Solución garantizada',
                'Evaluación de rendimiento'
            ],
            cta: 'Solicitar Diagnóstico'
        },
        {
            id: 'warranty',
            title: 'Garantías Extendidas',
            image: Garantias,
            alt: 'Garantías Extendidas JCB Azteca - Protección de inversión y mantenimiento prioritario',
            description: 'Protegemos tu inversión. Manejamos planes que ayudarán a extender el tiempo de vida y de operaciones en tu flotilla de maquinarias. Te brindamos la seguridad en completar tus tiempos de operaciones sin contratiempos.',
            features: [
                'Cobertura ampliada',
                'Mantenimiento prioritario',
                'Soporte técnico preferente',
                'Repuestos originales garantizados',
                'Funcionamiento óptimo por años'
            ],
            cta: 'Solicitar Garantía Extendida'
        },
        {
            id: 'reconstructions',
            title: 'Reconstrucciones Certificadas',
            image: Reconstrucciones,
            alt: 'Reconstrucciones Certificadas JCB Azteca - Más de 350 pruebas y 2000 piezas reemplazadas',
            description: 'Reconstrucciones con la finalidad de aumentar el tiempo de operaciones siempre bajo el monitoreo en tiempo real y actualizaciones constantes. Sometemos las máquinas bajo los estándares de calidad más altos.',
            features: [
                'Más de 350 pruebas y ajustes',
                'Reemplazo de más de 2000 piezas',
                'Monitoreo en tiempo real',
                'Garantía de calidad certificada',
                'Actualizaciones constantes'
            ],
            cta: 'Solicitar Reconstrucción'
        },
        {
            id: 'livelink',
            title: 'JCB LiveLink',
            image: Livelink,
            alt: 'JCB LiveLink - Sistema telemático para monitoreo de flota a distancia desde internet o móvil',
            description: 'Un innovador sistema de software que le permite administrar las máquinas de JCB a distancia a través de internet, correo electrónico o teléfono móvil. Acceda a alertas, informes de consumo de combustible e información histórica.',
            features: [
                'Monitoreo en tiempo real',
                'Alertas de mantenimiento',
                'Historial de operaciones',
                'Localización GPS',
                'Gestión de flota desde un centro seguro'
            ],
            cta: 'Solicitar Demo'
        }
    ];

    const buildServiceCartItem = (service) => ({
        id: `service-${service.id}`,
        type: 'service',
        category: 'service',
        name: service.title,
        price: 0,
        partNumber: null,
        hasPrice: false,
        serviceId: service.id,
    });

    const buildGenericServiceCartItem = () => ({
        id: 'service-generic',
        type: 'service',
        category: 'service',
        name: 'Servicio (solicitud general)',
        price: 0,
        partNumber: null,
        hasPrice: false,
        serviceId: null,
    });

    const buildServiceContext = (service) => ({
        source: 'services',
        serviceId: service.id,
        serviceName: service.title,
    });

    const buildGenericServiceContext = () => ({
        source: 'services',
        serviceId: null,
        serviceName: null,
    });

    const handleAddServiceAndGoToContact = (service) => {
        addToCart(buildServiceCartItem(service), buildServiceContext(service));
        navigate('/contact');
    };

    const handleAddGenericAndGoToContact = () => {
        addToCart(buildGenericServiceCartItem(), buildGenericServiceContext());
        navigate('/contact');
    };

    const isInCart = (serviceId) =>
        cartItems.some(item => item.id === `service-${serviceId}`);

    return (
        <div className="container mx-auto px-6 py-24">
            <Helmet>
                <title>Servicios y Postventa | JCB Azteca</title>
                <meta name="description" content="Servicio técnico especializado JCB: mantenimiento preventivo, diagnóstico y reparación, garantías extendidas, reconstrucciones certificadas y JCB LiveLink." />
                <meta name="keywords" content="servicio técnico JCB, mantenimiento preventivo JCB, reparación maquinaria JCB, JCB LiveLink, garantías extendidas JCB, reconstrucciones JCB, JCB Azteca" />
                <link rel="canonical" href="https://azteca-site.org/services" />

                <meta property="og:type" content="website" />
                <meta property="og:title" content="Servicios y Postventa | JCB Azteca" />
                <meta property="og:description" content="Servicio técnico especializado JCB: mantenimiento, diagnóstico, reparación, garantías y LiveLink." />
                <meta property="og:url" content="https://azteca-site.org/services" />
                <meta property="og:image" content="https://azteca-site.org/og/servicios-jcb-azteca.jpg" />
                <meta property="og:locale" content="es_MX" />
                <meta property="og:site_name" content="JCB Azteca" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Servicios y Postventa | JCB Azteca" />
                <meta name="twitter:description" content="Servicio técnico especializado JCB: mantenimiento, diagnóstico y reparación." />
                <meta name="twitter:image" content="https://azteca-site.org/og/servicios-jcb-azteca.jpg" />

                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "CollectionPage",
                        "name": "Servicios y Postventa | JCB Azteca",
                        "url": "https://azteca-site.org/services",
                        "description": "Servicio técnico especializado JCB: mantenimiento preventivo, diagnóstico y reparación, garantías extendidas, reconstrucciones certificadas y JCB LiveLink.",
                        "inLanguage": "es-MX",
                        "isPartOf": {
                            "@type": "WebSite",
                            "name": "JCB Azteca",
                            "url": "https://azteca-site.org/"
                        },
                        "hasPart": [
                            {
                                "@type": "Service",
                                "name": "Mantenimiento Preventivo JCB",
                                "url": "https://azteca-site.org/services#maintenance",
                                "description": "Programas de mantenimiento diseñados para prevenir fallas y prolongar la vida útil de tu maquinaria JCB.",
                                "provider": {
                                    "@type": "Organization",
                                    "name": "JCB Azteca",
                                    "url": "https://azteca-site.org/"
                                }
                            },
                            {
                                "@type": "Service",
                                "name": "Diagnóstico y Reparación de Fallas JCB",
                                "url": "https://azteca-site.org/services#diagnostic",
                                "description": "Diagnósticos precisos y reparaciones especializadas de maquinaria JCB con herramientas avanzadas.",
                                "provider": {
                                    "@type": "Organization",
                                    "name": "JCB Azteca",
                                    "url": "https://azteca-site.org/"
                                }
                            },
                            {
                                "@type": "Service",
                                "name": "Garantías Extendidas JCB",
                                "url": "https://azteca-site.org/services#warranty",
                                "description": "Planes de garantía extendida para proteger la inversión en flotillas JCB.",
                                "provider": {
                                    "@type": "Organization",
                                    "name": "JCB Azteca",
                                    "url": "https://azteca-site.org/"
                                }
                            },
                            {
                                "@type": "Service",
                                "name": "Reconstrucciones Certificadas JCB",
                                "url": "https://azteca-site.org/services#reconstructions",
                                "description": "Reconstrucciones con más de 350 pruebas, reemplazo de más de 2000 piezas y monitoreo en tiempo real.",
                                "provider": {
                                    "@type": "Organization",
                                    "name": "JCB Azteca",
                                    "url": "https://azteca-site.org/"
                                }
                            },
                            {
                                "@type": "Service",
                                "name": "JCB LiveLink",
                                "url": "https://azteca-site.org/services#livelink",
                                "description": "Sistema telemático para administrar máquinas JCB a distancia con alertas, informes e historial.",
                                "provider": {
                                    "@type": "Organization",
                                    "name": "JCB Azteca",
                                    "url": "https://azteca-site.org/"
                                }
                            }
                        ]
                    })}
                </script>
            </Helmet>

            <nav className="text-sm mb-8" aria-label="Breadcrumb">
                <Link to="/" className="text-text-muted hover:text-dark-primary">Inicio</Link>
                <span className="mx-2 text-text-muted">/</span>
                <span className="text-dark-primary font-medium">Servicios</span>
            </nav>

            <div className="relative bg-dark-primary rounded-lg overflow-hidden mb-16">
                <div className="absolute inset-0 bg-black/50"></div>
                <div className="relative z-10 px-8 md:px-16 py-16 md:py-24 text-white">
                    <span className="font-display text-xs uppercase tracking-luxury text-jcb-yellow font-semibold block mb-4">
                        Servicios Integrales
                    </span>
                    <h1 className="text-4xl md:text-6xl font-display font-bold mb-4 max-w-3xl">
                        Soluciones para tu Maquinaria
                    </h1>
                    <p className="text-lg text-white/80 max-w-2xl">
                        En JCB Azteca ofrecemos servicios especializados para garantizar el óptimo funcionamiento
                        de tus equipos y prolongar su vida útil.
                    </p>
                    <div className="mt-8 flex flex-wrap gap-4">
                        <button
                            type="button"
                            onClick={handleAddGenericAndGoToContact}
                            className="inline-flex items-center justify-center font-display text-xs uppercase tracking-luxury font-medium bg-jcb-yellow text-dark-primary hover:bg-yellow-500 px-8 py-3 transition-all duration-300"
                            title="Contactar a JCB Azteca"
                        >
                            Contactar ahora
                        </button>
                        <a
                            href="#maintenance"
                            className="inline-flex items-center justify-center font-display text-xs uppercase tracking-luxury font-medium border border-white/40 text-white hover:border-white hover:bg-white/10 px-8 py-3 transition-all duration-300"
                        >
                            Conocer servicios
                        </a>
                    </div>
                </div>
            </div>

            <div className="space-y-20">
                {services.map((service, index) => {
                    const inCart = isInCart(service.id);
                    return (
                        <div
                            key={service.id}
                            ref={sectionRefs[service.id]}
                            id={service.id}
                            className="scroll-mt-32"
                        >
                            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                                index % 2 === 1 ? 'lg:grid-flow-dense' : ''
                            }`}>
                                <div className={`bg-surface-subtle aspect-video rounded-lg overflow-hidden border border-border-subtle ${
                                    index % 2 === 1 ? 'lg:order-2' : ''
                                }`}>
                                    <img
                                        src={service.image}
                                        alt={service.alt}
                                        title={`${service.title} | JCB Azteca`}
                                        loading="lazy"
                                        width="800"
                                        height="450"
                                        className="w-full h-full object-cover"
                                    />
                                </div>

                                <div>
                                    <div className="flex items-center gap-4 mb-4">
                                        <span className="font-display text-xs uppercase tracking-luxury text-jcb-yellow font-semibold">
                                            Servicio {String(index + 1).padStart(2, '0')}
                                        </span>
                                    </div>
                                    <h2 className="text-3xl md:text-4xl font-display font-bold text-dark-primary mb-4">
                                        {service.title}
                                    </h2>
                                    <p className="text-text-muted text-lg mb-6 leading-relaxed">
                                        {service.description}
                                    </p>
                                    <div className="space-y-2 mb-6">
                                        {service.features.map((feature, i) => (
                                            <div key={i} className="flex items-center gap-3">
                                                <span className="text-jcb-yellow" aria-hidden="true">✓</span>
                                                <span className="text-text-muted text-sm">{feature}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-4">
                                        <button
                                            type="button"
                                            onClick={() => handleAddServiceAndGoToContact(service)}
                                            className="inline-flex items-center justify-center font-display text-xs uppercase tracking-luxury font-medium bg-dark-primary text-white hover:bg-black px-6 py-3 transition-all duration-300"
                                            title={`${service.cta} - JCB Azteca`}
                                        >
                                            {inCart ? 'Agregar +1' : service.cta}
                                        </button>
                                        {inCart && (
                                            <Link
                                                to="/cart"
                                                className="font-display text-xs uppercase tracking-luxury text-dark-primary hover:text-jcb-yellow transition-colors"
                                            >
                                                Ver carrito
                                            </Link>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {index < services.length - 1 && (
                                <div className="border-b border-border-subtle mt-20"></div>
                            )}
                        </div>
                    );
                })}
            </div>

            {cartItems.length > 0 && (
                <div className="mt-12 flex justify-end">
                    <Link
                        to="/cart"
                        className="font-display text-xs uppercase tracking-luxury font-medium bg-dark-primary text-white hover:bg-black px-6 py-3 transition-all"
                    >
                        Ir a cotizar ({cartItems.length} items)
                    </Link>
                </div>
            )}

            <div className="mt-16 bg-dark-primary p-8 md:p-12 text-center text-white rounded-lg">
                <h2 className="text-2xl font-display font-bold mb-4">
                    ¿Necesitas asistencia urgente?
                </h2>
                <p className="text-white/80 mb-6 max-w-2xl mx-auto">
                    Contamos con soporte técnico para emergencias. Contáctanos y nuestro equipo responderá lo antes posible.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                    <a
                        href="tel:+525548009000"
                        className="inline-flex items-center justify-center font-display text-xs uppercase tracking-luxury font-medium bg-jcb-yellow text-dark-primary hover:bg-yellow-500 px-8 py-3 transition-all duration-300"
                        title="Llamar a JCB Azteca"
                    >
                        Llamar ahora
                    </a>
                    <button
                        type="button"
                        onClick={handleAddGenericAndGoToContact}
                        className="inline-flex items-center justify-center font-display text-xs uppercase tracking-luxury font-medium border border-white/40 text-white hover:border-white hover:bg-white/10 px-8 py-3 transition-all duration-300"
                        title="Contactar a JCB Azteca"
                    >
                        Contactar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Services;